/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { FaRegFileAlt } from "react-icons/fa";
import { useRecoilValue } from "recoil";
import { v4 } from "uuid";
import { ContactApi } from "../../../api/Contacts";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { IForm } from "../../../generated/Api";
import useRowsPerPage, { ITableName } from "../../../hook/useRowsPerPage";
import { IContact } from "../../../types/Contact";
import {
  IContactEmailStatus,
  IContactEmailType,
} from "../../../types/EmailTypes";
import { ITag } from "../../../types/TagTypes";
import IconBerserkerMail from "../../Icon";
import SearchInput from "../../SearchInput";
import { TableFooter } from "../../StyledTable";
import Tag from "../../Tag";

export const SkeletonCard = styled.div`
  background-image: linear-gradient(
    -90deg,
    #f8f8f8 0%,
    #f0f0f0 50%,
    #f8f8f8 100%
  );
  background-size: 400% 400%;
  animation: shimmer 1.5s ease-in-out infinite;
  @keyframes shimmer {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: -135% 0%;
    }
  }
  &.white {
    background-image: linear-gradient(
      -90deg,
      #f8f8f8 0%,
      #f0f0f0 50%,
      #f8f8f8 100%
    );
  }
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  cursor: default;
`;

export const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: default;
  padding: 50px 0px;
`;

const NoteRow = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: 240px 1fr;
  grid-column-gap: 20px;
  width: 100%;
  background: #f8f8f8 0% 0% no-repeat padding-box;
  border-radius: 2px;
  margin-bottom: 10px;
  min-height: 80px;

  :last-child {
    margin-bottom: 0px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  height: 100%;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Between = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 40px;
`;

const Circle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: #fff;
  border: 1px solid;
  border-color: #868482;
  height: 40px;
  width: 40px;
  border-radius: 100%;

  flex-shrink: 0;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;

  .title {
    margin-bottom: 5px;
  }

  .info {
    display: flex;
    align-items: center;
    width: 80px;
  }

  .text {
    font-weight: 400;
  }
`;

const Text = styled.div`
  color: #333333;
  font-size: 14px;

  white-space: nowrap;
`;

const SubTitle = styled.div`
  color: #333333;
  font-size: 16px;
  font-weight: 600;

  &.Opened {
    color: #fb972e;
  }
`;

const noteTitle = css`
  font-size: 18px;
`;

interface SubjectProps {
  isEllipsis: boolean;
}

const Subject = styled.div<SubjectProps>`
  color: #333333;
  font-size: 16px;

  ${(props) =>
    props.isEllipsis &&
    css`
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      width: calc(100vw - 850px);
    `}
`;

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: start;

  grid-gap: 8px;

  overflow-y: auto;
`;
interface Props {
  contact: IContact;
}

export enum DateFilter {
  Recent = "recent",
  Older = "older",
}
export interface FilterEmail {
  status: IContactEmailStatus[];
  type: IContactEmailType[];
  date: DateFilter;
}

export interface EmailToContact {
  _id: string;
  subject: string;
  createdAt: string;
  type: IContactEmailType;
  status: IContactEmailStatus;
}

export const EMAIL_TYPE_NAME: { [key: string]: string } = {
  [IContactEmailType.CAMPAIGN]: "Campaign",
  [IContactEmailType.BROADCAST]: "Broadcast",
  [IContactEmailType.CONFIRMATION]: "Confirmation",
};
interface Props {
  contact: IContact;
}

interface FilterWebForms {
  date: DateFilter;
}

export type FormToContact = IForm & { appliedAt: Date };

export default function Webforms(props: Props) {
  const [infoFilter, setInfoFilter] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [forms, setForms] = useState<FormToContact[]>([]);
  const [filteredForms, setFilteredForms] = useState<FormToContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [filter, setFilter] = useState<FilterWebForms>({
    date: DateFilter.Recent,
  });
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.WEBFORMS
  );

  useEffect(() => {
    (async () => {
      const newForms = await ContactApi.readFormsByContact(props.contact._id);

      setForms(newForms);

      if (newForms.length < 1) {
        setIsEmpty(true);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    setFilteredForms(getFilteredEmails());
  }, [forms, infoFilter]);

  function getFilteredEmails() {
    if (!forms) return [];

    let emailsWithFilter: FormToContact[] = [...forms];

    return emailsWithFilter
      .filter((not) =>
        not.name.trim().toLowerCase().includes(infoFilter.toLowerCase().trim())
      )
      .sort((a, b) => {
        if (filter.date === DateFilter.Recent)
          return dayjs(b.createdAt).diff(a.createdAt);
        else return dayjs(a.createdAt).diff(b.createdAt);
      });
  }

  function handlePage(vle: number) {
    setPage(vle);
  }

  if (loading)
    return (
      <NotesContainer>
        <Between>
          <SubTitle css={noteTitle}>Webforms</SubTitle>

          <Buttons
            css={css`
              justify-content: flex-end;
            `}
          >
            <SearchInput
              css={css`
                width: 260px;
              `}
              placeholder="Search for webforms"
              value={infoFilter}
              handleChange={(vle) => setInfoFilter(vle)}
            />
            {/* <FilterEmails
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            /> */}
          </Buttons>
        </Between>

        {Array(5)
          .fill(0)
          .map((e) => (
            <SkeletonCard
              css={css`
                width: 100%;
                height: 80px;
                margin-bottom: 10px;
              `}
            />
          ))}
      </NotesContainer>
    );

  if (isEmpty) {
    return getEmptyContainer();
  }

  function getEmptyContainer() {
    let textToShow: string;
    if (infoFilter) {
      textToShow = "No webforms matching your criteria";
    } else {
      textToShow = "No webform to show";
    }

    return (
      <EmptyContainer>
        <IconBerserkerMail name="webforms-empty" />
        <SubTitle
          css={css`
            margin-top: 15.8px;
          `}
        >
          {textToShow}
        </SubTitle>
      </EmptyContainer>
    );
  }

  return (
    <NotesContainer>
      {(filteredForms.length > 0 || infoFilter) && (
        <Between>
          <SubTitle css={noteTitle}>Webforms</SubTitle>

          <Buttons
            css={css`
              justify-content: flex-end;
            `}
          >
            <SearchInput
              css={css`
                width: 260px;
              `}
              placeholder="Search for webforms"
              value={infoFilter}
              handleChange={(vle) => setInfoFilter(vle)}
            />
            {/* <FilterEmails
            filter={filter}
            handleChangeFilter={(vle) => setFilter(vle)}
          /> */}
          </Buttons>
        </Between>
      )}

      {filteredForms.length > 0 ? (
        <>
          {filteredForms
            .slice(
              page * (cachedRowsPerPage || rowsPerPage),
              page * (cachedRowsPerPage || rowsPerPage) +
                (cachedRowsPerPage || rowsPerPage)
            )
            .map((n) => {
              return <FormRow form={n} key={n.name} />;
            })}

          <TableFooter
            isSmaller
            page={page}
            length={filteredForms.length || 0}
            rowsPerPage={cachedRowsPerPage || rowsPerPage}
            setRowsPerPage={(vle: number) => {
              setRowsPerPage(vle);
              updateCachedRowsPerPage(vle);
            }}
            handlePage={handlePage}
          />
        </>
      ) : (
        getEmptyContainer()
      )}
    </NotesContainer>
  );
}

function FormRow(props: { form: FormToContact }) {
  const { form } = props;
  const tags = useRecoilValue(TagsAtom);
  const [formTags, setFormTags] = useState<ITag[]>([]);

  useEffect(() => {
    if (form?.tags) {
      const newTags = form.tags
        .map((t) => {
          return tags.find((tt) => tt._id === t);
        })
        .filter((t) => t);

      setFormTags(newTags);
    }
  }, [form]);

  return (
    <NoteRow>
      <Row>
        <Circle>
          <FaRegFileAlt size={20} color="#333333" />
        </Circle>
        <Column>
          <Text className="title">
            {dayjs(form.appliedAt).format("MMM D, YYYY [@] h:mm a (z)")}
          </Text>
          <SubTitle>{form.name}</SubTitle>
        </Column>
      </Row>
      <Row>
        <Column>
          <Text className="title">Tags</Text>
          <TagsWrapper>
            {formTags &&
              formTags.map((tag, index) => {
                if (index < 2) {
                  return (
                    <Tag
                      customStyle={{ maxWidth: "200px", margin: "0" }}
                      key={`${tag?._id}_${tag?.name}`}
                      tag={tag}
                    />
                  );
                } else if (index === 2 && formTags.length === 3) {
                  return (
                    <Tag
                      customStyle={{ maxWidth: "200px", margin: "0" }}
                      key={`${tag?._id}_${tag?.name}`}
                      tag={tag}
                    />
                  );
                } else if (index === 2) {
                  return (
                    <div data-tip data-for={tag?._id}>
                      <Tag
                        customStyle={{ maxWidth: "200px", margin: "0" }}
                        key={`${tag?._id}_${tag?.name}`}
                        tag={{
                          ...tag,
                          _id: v4(),
                          name: `+${formTags.length - 2} more`,
                        }}
                        tooltip={formTags
                          .slice(2, formTags.length)
                          .map(({ category, name }) => `${category} -> ${name}`)
                          .join("\n")}
                      />
                    </div>
                  );
                }
              })}
          </TagsWrapper>
        </Column>
      </Row>
    </NoteRow>
  );
}
